import { useEffect, useRef, useState } from "react";
import Link from "next/link";

import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "@/services/notificationService";
import { ROUTE } from "@/route/app_routes";

const timeAgo = (iso) => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationBell = ({ user }) => {

  const loggedIn = !!user;
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // The dropdown's reveal was originally pure CSS (:hover on the <li>, via the shared
  // %rbt-hover-dropdown-type-1 mixin) -- fine with a mouse, but a tap on a touch screen never
  // establishes a lasting :hover state, so there was no way to open this at all on mobile
  // ("click the bell -- nothing happens"). `open` drives an explicit inline style below,
  // bypassing that mixin's opacity/visibility/clip entirely rather than fighting its
  // specificity, so it now works the same way by tap, click, or hover everywhere.
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!loggedIn) return;
    getUnreadCount()
      .then((countRes) => setUnreadCount(countRes.data.unread_count))
      .catch(() => {});
  }, [loggedIn]);

  const loadNotifications = () => {
    if (loaded || !loggedIn) return;
    getNotifications()
      .then((res) => {
        setNotifications(res.data.data.slice(0, 6));
        setLoaded(true);
      })
      .catch(() => {});
  };

  // Click-outside and Escape both close it -- the same "tap the backdrop, or Esc, to dismiss"
  // expectation as the site's other overlays (Search.js, the mobile off-canvas menus).
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const toggleOpen = (e) => {
    if (!loggedIn) return; // real navigation to ROUTE.login instead, handled by the href below
    e.preventDefault();
    setOpen((prev) => {
      const next = !prev;
      if (next) loadNotifications();
      return next;
    });
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      // no-op -- non-critical UI state
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      // no-op -- non-critical UI state
    }
  };

  return (
    <li
      className="access-icon rbt-mini-cart rbt-user-wrapper right-align-dropdown"
      ref={rootRef}
      onMouseEnter={loadNotifications}
    >
      <Link
        className="rbt-cart-sidenav-activation rbt-round-btn"
        href={loggedIn ? "#" : ROUTE.login}
        title="Notifications"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <i className="feather feather-bell"></i>
        {loggedIn && unreadCount > 0 && <span className="rbt-cart-count">{unreadCount}</span>}
      </Link>

      {loggedIn && (
        <div
          className="rbt-user-menu-list-wrapper rbt-notification-dropdown"
          // Guaranteed to work regardless of the shared hover mixin's own opacity/visibility/
          // clip values (see the `open` state comment above) -- inline styles always win the
          // cascade, so there's no specificity fight to keep re-litigating here.
          style={open ? { opacity: 1, visibility: "visible", clip: "auto", pointerEvents: "auto" } : undefined}
        >
          <div className="inner">
            <div className="rbt-notification-dropdown__head">
              <span className="name">Notifications</span>
              {unreadCount > 0 && (
                <button type="button" className="rbt-btn-link color-primary" onClick={handleMarkAllRead}>
                  Mark all read
                </button>
              )}
            </div>

            {!loaded ? (
              <div className="rbt-notification-dropdown__list">
                {[1, 2, 3].map((i) => (
                  <div className="rbt-notification-dropdown__skeleton" key={i}>
                    <span></span>
                    <span></span>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="rbt-notification-dropdown__empty">
                <i className="feather-bell"></i>
                <p>You&apos;re all caught up.</p>
              </div>
            ) : (
              <div className="rbt-notification-dropdown__list">
                {notifications.map((n) => (
                  <a
                    href="#"
                    key={n.id}
                    className={`rbt-notification-dropdown__item ${n.read_at ? "" : "is-unread"}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!n.read_at) handleMarkRead(n.id);
                    }}
                  >
                    <span className="rbt-notification-dropdown__dot" aria-hidden="true"></span>
                    <span className="rbt-notification-dropdown__text">
                      <strong>{n.data.title}</strong>
                      <span className="rbt-notification-dropdown__msg">{n.data.message}</span>
                      <span className="rbt-notification-dropdown__time">{timeAgo(n.created_at)}</span>
                    </span>
                  </a>
                ))}
              </div>
            )}

            <Link href={ROUTE.notifications} className="rbt-notification-dropdown__footer" onClick={() => setOpen(false)}>
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </li>
  );
};

export default NotificationBell;
